// Tiny formatting helpers shared across pages.
//
// Zahlen mit deutschem Komma (docs/gestaltung.md, "Deutsche Zahlen").
// Bis 28.09.2026 schrieben formatBytes, formatWatts und formatKwh einen
// Punkt ("10.9 TB") -- auf jeder Seite, die Groessen oder Leistung zeigt.
const komma = (s: string): string => s.replace(".", ",");

/** Zahl mit deutschem Komma und fester Stellenzahl: 72.24 -> "72,2".
 *  Ab 1000 mit Tausenderpunkt ("5.067"). Für Kennzahlen, bei denen die
 *  Einheit getrennt daneben steht. */
export function formatZahl(v: number | null | undefined, stellen = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

export function formatWatts(w: number | null | undefined): string {
  if (w == null) return "—";
  if (Math.abs(w) >= 1000) return komma((w / 1000).toFixed(2)) + " kW";
  return komma(String(Math.round(w * 10) / 10)) + " W";
}

export function formatKwh(v: number | null | undefined): string {
  if (v == null) return "—";
  return komma((Math.round(v * 100) / 100).toFixed(2)) + " kWh";
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  const u = ["B", "kB", "MB", "GB", "TB", "PB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  if (i === 0) return `${v} B`;
  return komma(v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0)) + " " + u[i];
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 1) return Math.round(seconds * 1000) + " ms";
  // Deutsches Komma: "1,2 s", nicht "1.2 s".
  // Erst runden, dann teilen: 179,99 s wurden sonst zu „2m 60s" (Minuten
  // abgeschnitten, Sekunden gerundet), 59,96 s zu „60,0 s".
  if (seconds < 59.95) return seconds.toFixed(1).replace(".", ",") + " s";
  const ganz = Math.round(seconds);
  const m = Math.floor(ganz / 60);
  const s = ganz % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// formatUptime ist die Anzeige fuer "Host laeuft seit X" -- in Stunden bis
// 48h, danach in Tagen mit optionalem Rest-Stunden-Suffix. Bewusst andere
// Skala als formatDuration (das ist fuer Task-Laufzeiten, oft im Sekunden-
// bis Minuten-Bereich); ein Host der 56 Tage durch ist will kein "1366h
// 30m" zeigen.
export function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "—";
  // Zahl und Einheit getrennt, Minuten als "min" -- wie formatRelative
  // ("47 h", nicht "47h"; ein blankes "m" liest sich wie Meter).
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h`;
  const d = Math.floor(h / 24);
  const restH = h % 24;
  return restH > 0 ? `${d} d ${restH} h` : `${d} d`;
}

// formatRelative turns an ISO-timestamp into "vor 12 s" / "vor 3 min".
// Clock-skew between Browser, Server und Agent kann dazu führen
// dass ein Event mit ts in der Zukunft ankommt — vorher hat
// `Math.max(0, ...)` das fälschlich als "gerade eben" angezeigt
// und so den Eindruck eines Live-Events erweckt, der gar nicht
// gerade passiert. Stattdessen ehrlich: bei Skew > 1 min zeigen
// wir den absoluten Zeitstempel.
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const seconds = Math.round((now.getTime() - t) / 1000);
  if (seconds < -60) return formatAbsoluteShort(iso);
  // Sub-Minuten konsistent als "vor X s" -- inkl. 0 fuer "jetzt"
  // ohne Sonderfall-String, damit die Zeit-Spalte (tabular-nums,
  // fixe Breite) nicht durch "gerade eben" aus dem Raster faellt.
  // clamp negativer Skew (-60..0) auf 0.
  if (seconds < 60) return `vor ${Math.max(0, seconds)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `vor ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `vor ${days} d`;
  // Ab einer Woche schalten wir auf groebere Einheiten -- lange Zahlen
  // wie "2566 d" sind weder schoen noch lesbar. Deutsche Kuerzel: ein
  // blankes "m" fuer Monate las sich wie Minuten ("vor 4 m" an einem
  // Ereignis vom Mai, 03.10.2026).
  if (days < 30) return `vor ${Math.round(days / 7)} Wo.`;
  if (days < 365) return `vor ${Math.round(days / 30)} Mon.`;
  return `vor ${Math.round(days / 365)} J.`;
}

// formatAbsoluteShort gives a compact local-time display for the
// clock-skew fallback above: "29.04. 10:59" (today) or
// "29.04.2026 10:59" (different year). Locale-tolerant — uses the
// browser's default formatting.
export function formatAbsoluteShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  if (sameYear) return `${dd}.${mm}. ${hh}:${min}`;
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${min}`;
}

// formatAbsoluteSeconds: wie formatAbsoluteShort, mit Sekunden --
// "02.10. 01:30:24". Für Verläufe, deren Einträge Sekunden auseinander
// liegen (Link-Wechsel eines Flaps).
export function formatAbsoluteSeconds(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${formatAbsoluteShort(iso)}:${ss}`;
}

// formatAbsolute gives the full local timestamp in German 24h format
// — "30.04.2026, 18:17:06". Used in detail-pages / event-lists where
// the exact time matters and a relative "vor 1 d" sits next to it.
export function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("de-DE", { hour12: false });
}
